import { Link } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import AnimatedCounter from '@/components/AnimatedCounter'
import TestimonialsScroll from '@/components/TestimonialsScroll'
import { motion } from 'framer-motion'
import { 
  Home as HomeIcon, 
  HardHat, 
  Droplets, 
  PaintBucket, 
  Camera,
  Building2,
  Shield,
  Users,
  Award,
  Clock
} from 'lucide-react'

// Total services count: 15
const totalServicesCount = 15

const serviceCategories = [
  {
    title: "Roofing Services",
    icon: HomeIcon,
    services: [
      { name: "Roofing", description: "Expert roofing for all property types", path: "/services/roofing" },
      { name: "Flat Roofing", description: "Durable flat roofing solutions", path: "/services/flat-roofing" },
      { name: "Leadwork", description: "Traditional and modern leadwork", path: "/services/leadwork" },
      { name: "Chimney Demolition", description: "Safe chimney removal services", path: "/services/chimney-demolition" }
    ]
  },
  {
    title: "Building & Structural",
    icon: Building2,
    services: [
      { name: "General Building", description: "Complete building services", path: "/services/general-building" },
      { name: "Re-pointing", description: "Restore your brickwork", path: "/services/repointing" },
      { name: "Lime Pointing", description: "Traditional lime pointing", path: "/services/lime-pointing" },
      { name: "Render Repairs", description: "Expert render repair & renewal", path: "/services/render-repairs" },
      { name: "Guttering & Fascias", description: "Protect from water damage", path: "/services/guttering-fascias" }
    ]
  },
  {
    title: "Interior & Exterior Services",
    icon: PaintBucket,
    services: [
      { name: "Plastering", description: "Smooth, flawless finishes", path: "/services/plastering" },
      { name: "Decorating", description: "Interior and exterior painting", path: "/services/decorating" },
      { name: "Damp Proofing", description: "Protect from damp damage", path: "/services/damp-proofing" },
      { name: "Loft Insulation", description: "Energy-efficient insulation upgrades", path: "/services/loft-insulation" },
      { name: "Sash Window Repairs", description: "Restore traditional windows", path: "/services/sash-window-repairs" }
    ]
  }
]

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
}

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1
  }
}

function Home() {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-accent/30 via-white to-secondary overflow-hidden min-h-[500px] md:min-h-[600px] lg:min-h-[700px] flex items-center">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.3 }}
          transition={{ duration: 1 }}
          className="absolute inset-0 bg-[url('/images/hero-image.JPG')] bg-cover"
          style={{ 
            backgroundPosition: '50% 80%',
            backgroundSize: 'cover'
          }}
        />
        <div className="container mx-auto px-4 relative z-10 py-16 md:py-20 lg:py-24 w-full">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
              Building Excellence Since 1980
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-700">
              Family-run roofing and building contractors delivering quality, 
              trust, and exceptional craftsmanship to Brighton & Hove
            </p>
            <div className="flex gap-4 justify-center">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button asChild size="lg" className="text-lg px-8 py-6 shadow-lg">
                  <Link to="/contact">Get Your Free Quote</Link>
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button asChild variant="outline" size="lg" className="text-lg px-8 py-6">
                  <Link to="/services">View Our Services</Link>
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section with Animated Counters */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-4 gap-8 text-center"
          >
            <motion.div variants={itemVariants} className="group">
              <div className="flex flex-col items-center">
                <Clock className="h-12 w-12 text-primary mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-5xl font-bold text-gray-900 mb-2">
                  <AnimatedCounter from={0} to={45} duration={2} />
                </h3>
                <p className="text-gray-600 font-medium">Years of Service</p>
              </div>
            </motion.div>
            
            <motion.div variants={itemVariants} className="group">
              <div className="flex flex-col items-center">
                <Users className="h-12 w-12 text-primary mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-5xl font-bold text-gray-900 mb-2">
                  <AnimatedCounter from={0} to={10000} duration={2} suffix="+" formatNumber={true} />
                </h3>
                <p className="text-gray-600 font-medium">Happy Customers</p>
              </div>
            </motion.div>
            
            <motion.div variants={itemVariants} className="group">
              <div className="flex flex-col items-center">
                <Shield className="h-12 w-12 text-primary mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-5xl font-bold text-gray-900 mb-2">
                  <AnimatedCounter from={0} to={100} duration={2.5} suffix="%" />
                </h3>
                <p className="text-gray-600 font-medium">Insured</p>
              </div>
            </motion.div>
            
            <motion.div variants={itemVariants} className="group">
              <div className="flex flex-col items-center">
                <Award className="h-12 w-12 text-primary mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-5xl font-bold text-gray-900 mb-2">
                  <AnimatedCounter from={0} to={totalServicesCount} duration={1.5} />
                </h3>
                <p className="text-gray-600 font-medium">Specialist Services</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4">Our Services</h2>
            <p className="text-xl text-gray-600">Comprehensive solutions for all your property needs</p>
          </motion.div>
          
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-8"
          >
            {serviceCategories.map((category) => {
              const Icon = category.icon
              return (
                <motion.div key={category.title} variants={itemVariants}>
                  <Card className="hover:shadow-xl transition-all duration-300 h-full border-2 hover:border-primary/20">
                    <CardHeader className="pb-4">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-3 bg-primary/10 rounded-lg">
                          <Icon className="h-8 w-8 text-primary" />
                        </div>
                        <CardTitle className="text-xl">{category.title}</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3">
                        {category.services.map((service) => (
                          <li key={service.path}>
                            <Link 
                              to={service.path}
                              className="block p-3 rounded-lg hover:bg-accent transition-all duration-200 group"
                            >
                              <h4 className="font-semibold text-gray-900 group-hover:text-primary transition-colors">
                                {service.name}
                              </h4>
                              <p className="text-sm text-gray-600">{service.description}</p>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>
              )
            })}
          </motion.div>

          {/* Drone Surveys CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12"
          >
            <Card className="bg-gradient-to-r from-primary/5 to-secondary/5 border-2 border-primary/20">
              <CardContent className="p-8">
                <div className="flex items-center justify-between gap-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-white rounded-lg shadow-md flex-shrink-0">
                      <Camera className="h-8 w-8 text-primary" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-2xl font-bold text-gray-900">Drone Roof Surveys</h3>
                      <p className="text-gray-700">
                        Safe, detailed inspections without expensive scaffolding
                      </p>
                      <p className="text-gray-600">
                        Get high-resolution imagery and comprehensive reports to identify issues 
                        quickly and cost-effectively.
                      </p>
                    </div>
                  </div>
                  <Button asChild size="lg" className="flex-shrink-0">
                    <Link to="/services/drone-surveys">Learn More</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Infinite Scroll */}
      <TestimonialsScroll />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary to-primary/90 text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-4xl font-bold mb-4">Ready to Start Your Project?</h2>
            <p className="text-xl mb-8 opacity-90">
              Get in touch today for a free, no-obligation quote from our expert team
            </p>
            <div className="flex gap-4 justify-center">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button asChild size="lg" variant="secondary" className="text-lg px-8 py-6">
                  <Link to="/contact">Get Free Quote</Link>
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6 bg-white/10 text-white border-white hover:bg-white/20">
                  <a href="tel:01273326200">Call Now</a>
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default Home